import type { AttackResult } from "../types/combat.js";
import { resolveAttackRoll } from "../combat/attackResolver.js";
import { rollD20 } from "../combat/dice.js";

/**
 * Shared spell-attack / saving-throw pipeline (S04).
 *
 * Spell attacks reuse the physical `resolveAttackRoll` — only the inputs are
 * different (spellcasting modifier in place of STR/DEX, no weapon bonus).
 * Saving throws are the mirror image: the target rolls a d20, and beats
 * (≥) the caster's spell save DC to avoid or halve the effect.
 *
 * Both helpers are pure — no EventBus emission, no PartyStore mutation. The
 * CombatManager is responsible for turning the results into feed lines and
 * damage applications.
 */

export interface SpellAttackInput {
  readonly spellcastingModifier: number;
  readonly proficiencyBonus: number;
  readonly targetAC: number;
  /** Flat bonus (e.g. Bless, Guided Bolt advantage when S14 lands). */
  readonly hitBonus?: number;
}

export function resolveSpellAttack(input: SpellAttackInput): AttackResult {
  return resolveAttackRoll({
    abilityModifier: input.spellcastingModifier,
    proficiencyBonus: input.proficiencyBonus,
    targetAC: input.targetAC,
    ...(input.hitBonus !== undefined ? { hitBonus: input.hitBonus } : {}),
  });
}

/** Standard 5e DC formula. */
export function spellSaveDc(spellcastingModifier: number, proficiencyBonus: number): number {
  return 8 + spellcastingModifier + proficiencyBonus;
}

export interface SavingThrowInput {
  readonly dc: number;
  readonly targetSaveModifier: number;
  /** True when this spell deals half damage on success (save-for-half rider). */
  readonly halfOnSave?: boolean;
  /** Flat modifier (advantage proxy for v1). */
  readonly saveBonus?: number;
}

export interface SavingThrowResult {
  /** Raw d20 roll — nat 1 always fails, nat 20 always succeeds. */
  readonly d20: number;
  readonly total: number;
  readonly dc: number;
  /** True when the target beat the DC (or rolled nat 20). */
  readonly success: boolean;
  /**
   * What the caller should apply:
   *   - "none"  — no effect (target succeeded, no half rider)
   *   - "half"  — target succeeded but spell has half-on-save
   *   - "full"  — target failed; apply full damage / effect
   */
  readonly outcome: "none" | "half" | "full";
}

export function resolveSavingThrow(input: SavingThrowInput): SavingThrowResult {
  const d20 = rollD20();
  const total = d20 + input.targetSaveModifier + (input.saveBonus ?? 0);

  // Nat-1 auto-fail, nat-20 auto-succeed per 5e SRD.
  const autoSuccess = d20 === 20;
  const autoFail = d20 === 1;
  const success = autoSuccess || (!autoFail && total >= input.dc);

  const outcome: SavingThrowResult["outcome"] = success
    ? input.halfOnSave
      ? "half"
      : "none"
    : "full";

  return { d20, total, dc: input.dc, success, outcome };
}
